/**
 * PlaybackPage - Reproduzir gravacoes do SD card de cameras JT1078
 *
 * Fluxo:
 * 1. Seleciona periodo (data/hora inicio e fim)
 * 2. Busca arquivos no SD card (0x9205)
 * 3. Seleciona gravacao e reproduz (0x9201)
 * 4. Controla playback (0x9202): pause, speed, seek, stop
 *
 * Rota: /camera/:deviceId/playback
 */

import {
  useState, useCallback, useEffect,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Button,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import VideocamIcon from '@mui/icons-material/Videocam';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SdStorageIcon from '@mui/icons-material/SdStorage';

import JessibucaPlayer from '../components/JessibucaPlayer';
import {
  usePlaybackCommand,
  PLAYBACK_STATE,
} from '../hooks/usePlaybackCommand';
import { SPEED_MULTIPLIER } from '../hooks/useVideoCommand';
import { useCameraCapabilities } from '../hooks/useCameraCapabilities';
import { CHANNEL_NAMES } from '../utils/cameraConstants';

// ============================================
// Styled Components
// ============================================

const PageContainer = styled(Box)(({ theme }) => ({
  height: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
}));

const Header = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  borderRadius: 0,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  overflow: 'auto',
  gap: theme.spacing(1.5),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

const Section = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
}));

const PlayerContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  aspectRatio: '16/9',
  maxHeight: '45vh',
  backgroundColor: '#000',
  borderRadius: 4,
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    maxHeight: '50vh',
  },
}));

const ControlBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 4,
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
  },
}));

const SpeedButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ isActive, theme }) => ({
  minWidth: 40,
  padding: '4px 8px',
  fontSize: '0.75rem',
  color: isActive ? theme.palette.success.main : theme.palette.text.secondary,
  borderColor: isActive ? theme.palette.success.main : theme.palette.divider,
  backgroundColor: isActive ? alpha(theme.palette.success.main, 0.1) : 'transparent',
  '&:hover': {
    backgroundColor: isActive ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.text.primary, 0.05),
    borderColor: isActive ? theme.palette.success.main : theme.palette.action.hover,
  },
}));

// Speed options
const SPEED_OPTIONS = [
  { label: '1x', value: SPEED_MULTIPLIER.X1 },
  { label: '2x', value: SPEED_MULTIPLIER.X2 },
  { label: '4x', value: SPEED_MULTIPLIER.X4 },
  { label: '8x', value: SPEED_MULTIPLIER.X8 },
  { label: '16x', value: SPEED_MULTIPLIER.X16 },
];

/**
 * Format seconds duration to MM:SS or HH:MM:SS
 */
const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '--:--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Format date for display
 */
const formatDate = (date) => {
  if (!date) return '--';
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// ============================================
// Main Component
// ============================================
const PlaybackPage = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux state
  const device = useSelector((state) => state.devices.items[deviceId]);

  // Camera capabilities (channel count)
  const { channelCount } = useCameraCapabilities(deviceId, { autoQuery: true });

  // Playback hook
  const {
    state: playbackState,
    resources,
    currentSpeed,
    error: playbackError,
    isLoading,
    isPlaying,
    isPaused,
    queryResources,
    startPlayback,
    pausePlayback,
    resumePlayback,
    changeSpeed,
    stopPlayback,
    reset,
  } = usePlaybackCommand(deviceId);

  // Local state
  const [channel, setChannel] = useState(1);
  const [startTime, setStartTime] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() - 1);
    d.setMinutes(0, 0, 0);
    return d;
  });
  const [endTime, setEndTime] = useState(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    return d;
  });
  const [selectedResource, setSelectedResource] = useState(null);

  // Handle back
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Handle search
  const handleSearch = useCallback(() => {
    setSelectedResource(null);
    queryResources(channel, startTime, endTime);
  }, [channel, startTime, endTime, queryResources]);

  // Handle play selected resource
  const handlePlaySelected = useCallback(() => {
    if (!selectedResource) return;

    startPlayback(
      selectedResource.channel,
      selectedResource.startTime,
      selectedResource.endTime,
    );
  }, [selectedResource, startPlayback]);

  // Derive active channel for playback controls
  const activeChannelForPlayback = selectedResource?.channel || channel;

  // Handle pause/resume toggle
  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      pausePlayback(activeChannelForPlayback);
    } else if (isPaused) {
      resumePlayback(activeChannelForPlayback);
    }
  }, [isPlaying, isPaused, pausePlayback, resumePlayback, activeChannelForPlayback]);

  // Handle stop
  const handleStop = useCallback(() => {
    stopPlayback(activeChannelForPlayback);
  }, [stopPlayback, activeChannelForPlayback]);

  // Handle speed change
  const handleSpeedChange = useCallback((speed) => {
    changeSpeed(activeChannelForPlayback, speed);
  }, [changeSpeed, activeChannelForPlayback]);

  // Can show player
  const showPlayer = playbackState === PLAYBACK_STATE.PLAYING
    || playbackState === PLAYBACK_STATE.PAUSED;

  // Cleanup on unmount - stop playback on the device before resetting local state
  useEffect(() => () => {
    stopPlayback();
    reset();
  }, [stopPlayback, reset]);

  // Device not found
  if (!device) {
    return (
      <PageContainer>
        <Header>
          <IconButton onClick={handleBack} sx={{ color: 'inherit' }}>
            <ArrowBackIcon />
          </IconButton>
        </Header>
        <ContentArea>
          <Alert severity="error" sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
            Dispositivo nao encontrado
          </Alert>
        </ContentArea>
      </PageContainer>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        {/* Header */}
        <Header elevation={0}>
          <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <IconButton onClick={handleBack} sx={{ color: 'inherit' }}>
              <ArrowBackIcon />
            </IconButton>

            {!isMobile && <DirectionsCarIcon sx={{ color: tokens?.text?.tertiary }} />}

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                {device.name}
              </Typography>
              {!isMobile && (
                <Typography variant="caption" sx={{ color: tokens?.text?.tertiary }}>
                  {device.uniqueId}
                </Typography>
              )}
            </Box>

            <Chip
              icon={<SdStorageIcon sx={{ fontSize: 14 }} />}
              label={isMobile ? 'REC' : 'GRAVAÇÕES'}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.warning.main, 0.2),
                color: theme.palette.warning.main,
                fontWeight: 600,
                '& .MuiChip-icon': { color: theme.palette.warning.main },
              }}
            />
          </Stack>

          <Button
            variant="outlined"
            size="small"
            startIcon={<VideocamIcon />}
            onClick={() => navigate(`/camera/${deviceId}`)}
            sx={{ color: 'inherit', borderColor: theme.palette.divider }}
          >
            Ao Vivo
          </Button>
        </Header>

        <ContentArea>
          {/* Search Section */}
          <Section>
            <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
              Buscar Gravacoes
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
              {/* Channel selector */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: theme.palette.text.secondary }}>Canal</InputLabel>
                <Select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  label="Canal"
                  sx={{
                    color: theme.palette.text.primary,
                    '.MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.action.hover },
                    '.MuiSvgIcon-root': { color: theme.palette.text.secondary },
                  }}
                >
                  {Array.from({ length: channelCount }, (_, i) => i + 1).map((ch) => (
                    <MenuItem key={ch} value={ch}>
                      {CHANNEL_NAMES[ch] || `Canal ${ch}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Start time */}
              <DateTimePicker
                label="Inicio"
                value={startTime}
                onChange={setStartTime}
                format="dd/MM/yyyy HH:mm"
                ampm={false}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        color: theme.palette.text.primary,
                        '& fieldset': { borderColor: theme.palette.divider },
                        '&:hover fieldset': { borderColor: theme.palette.action.hover },
                      },
                      '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
                      '& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
                    },
                  },
                }}
              />

              {/* End time */}
              <DateTimePicker
                label="Fim"
                value={endTime}
                onChange={setEndTime}
                format="dd/MM/yyyy HH:mm"
                ampm={false}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        color: theme.palette.text.primary,
                        '& fieldset': { borderColor: theme.palette.divider },
                        '&:hover fieldset': { borderColor: theme.palette.action.hover },
                      },
                      '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
                      '& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
                    },
                  },
                }}
              />

              {/* Search button */}
              <Button
                variant="contained"
                startIcon={isLoading && playbackState === PLAYBACK_STATE.QUERYING
                  ? <CircularProgress size={16} color="inherit" />
                  : <SearchIcon />}
                onClick={handleSearch}
                disabled={isLoading}
                sx={{
                  backgroundColor: theme.palette.success.main,
                  '&:hover': { backgroundColor: theme.palette.success.dark },
                  whiteSpace: 'nowrap',
                }}
              >
                Buscar
              </Button>
            </Stack>
          </Section>

          {/* Error display */}
          {playbackError && (
            <Alert
              severity={resources.length > 0 ? 'warning' : 'error'}
              onClose={() => {}}
              sx={{ '& .MuiAlert-action': { display: 'none' } }}
            >
              {playbackError}
            </Alert>
          )}

          {/* Resources Table */}
          {resources.length > 0 && (
            <Section>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
                  Gravacoes Encontradas ({resources.length})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  onClick={handlePlaySelected}
                  disabled={!selectedResource || isLoading}
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    '&:hover': { backgroundColor: theme.palette.success.dark },
                    '&.Mui-disabled': { backgroundColor: theme.palette.action.disabledBackground, color: theme.palette.text.disabled },
                  }}
                >
                  Reproduzir
                </Button>
              </Stack>

              <TableContainer sx={{ maxHeight: 250, overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary, width: 40 }} />
                      <TableCell sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary }}>Canal</TableCell>
                      {!isMobile && <TableCell sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary }}>Inicio</TableCell>}
                      {!isMobile && <TableCell sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary }}>Fim</TableCell>}
                      <TableCell sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary }}>Duracao</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resources.map((res) => (
                      <TableRow
                        key={`${res.channel}-${res.startTimeStr}`}
                        hover
                        selected={selectedResource === res}
                        onClick={() => setSelectedResource(res)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.05) },
                          '&.Mui-selected': { backgroundColor: alpha(theme.palette.success.main, 0.1) },
                          '&.Mui-selected:hover': { backgroundColor: alpha(theme.palette.success.main, 0.15) },
                        }}
                      >
                        <TableCell>
                          <Radio
                            checked={selectedResource === res}
                            size="small"
                            sx={{ color: theme.palette.text.disabled, '&.Mui-checked': { color: theme.palette.success.main } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={CHANNEL_NAMES[res.channel] || `CH${res.channel}`}
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.info.main, 0.2),
                              color: theme.palette.info.main,
                              fontSize: '0.7rem',
                            }}
                          />
                        </TableCell>
                        {!isMobile && (
                          <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                            {formatDate(res.startTime)}
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                            {formatDate(res.endTime)}
                          </TableCell>
                        )}
                        <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                          {formatDuration(res.duration)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Section>
          )}

          {/* Video Player */}
          {showPlayer && (
            <Section>
              <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                Reproducao
                {selectedResource && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: tokens?.text?.tertiary || theme.palette.text.disabled }}>
                    {CHANNEL_NAMES[selectedResource.channel]}
                    {' - '}
                    {formatDate(selectedResource.startTime)}
                    {' a '}
                    {formatDate(selectedResource.endTime)}
                  </Typography>
                )}
              </Typography>

              {/* Player */}
              <PlayerContainer>
                <JessibucaPlayer
                  deviceImei={device.uniqueId}
                  channel={selectedResource?.channel || channel}
                  autoplay
                  playbackMode
                />
              </PlayerContainer>

              {/* Playback Controls */}
              <ControlBar sx={{ mt: 1 }}>
                {/* Stop */}
                <Tooltip title="Parar">
                  <IconButton
                    onClick={handleStop}
                    sx={{ color: theme.palette.error.main }}
                  >
                    <StopIcon />
                  </IconButton>
                </Tooltip>

                {/* Play/Pause */}
                <Tooltip title={isPlaying ? 'Pausar' : 'Continuar'}>
                  <span>
                    <IconButton
                      onClick={handleTogglePlay}
                      disabled={!isPlaying && !isPaused}
                      sx={{ color: 'inherit' }}
                    >
                      {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Separator */}
                <Box sx={{ width: 1, height: 24, backgroundColor: theme.palette.divider, mx: 1 }} />

                {/* Speed controls */}
                {!isMobile && (
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 0.5 }}>
                    Velocidade:
                  </Typography>
                )}
                {SPEED_OPTIONS.map(({ label, value }) => (
                  <SpeedButton
                    key={value}
                    variant="outlined"
                    size="small"
                    isActive={currentSpeed === value}
                    onClick={() => handleSpeedChange(value)}
                    disabled={!isPlaying && !isPaused}
                  >
                    {label}
                  </SpeedButton>
                ))}
              </ControlBar>
            </Section>
          )}
        </ContentArea>
      </PageContainer>
    </LocalizationProvider>
  );
};

export default PlaybackPage;
